// pages/goods-receipts/edit.jsx (EditGR.jsx)
import { useEffect, useMemo, useRef, useState } from "react"
import { useLoaderData, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { useSWRConfig } from "swr"
import { Transition } from "components"
import { usePage } from "contexts/page-context"
import { useAuth } from "contexts/auth-context"

// ✅ nanti kamu buat Form GR mirip PO
import Form from "./form"

function todayISO() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

// ✅ fetch helper (konsisten)
async function apiFetch(path, { method = "GET", body } = {}) {
  const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
  const url = `${base}${path}`

  const res = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : null,
  })

  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = json?.message || "Request gagal"
    const err = json?.error ? `: ${json.error}` : ""
    throw new Error(msg + err)
  }
  return json
}

export default function EditGR() {
  const { id } = useParams() // /goods-receipts/:id/edit
  const isEdit = Boolean(id)

  const { can, user } = useAuth()
  const { cache } = useSWRConfig()
  const { setTransition } = usePage()
  const { setTitle } = useOutletContext()

  const location = useLocation()
  const navigate = useNavigate()

  // loader idealnya sudah ngasih data GR terbaru
  const loaderData = useLoaderData()

  // dukung beberapa bentuk:
  // - {data:{...}}
  // - {goods_receipt:{...}}
  // - {...}
  const grFromLoader = loaderData?.data || loaderData?.goods_receipt || loaderData || null

  const [gr, setGr] = useState(grFromLoader)
  const [errors, setErrors] = useState({})

  const status = String(gr?.status || "DRAFT").toUpperCase()
  const isDraft = status === "DRAFT"
  const isLocked = !isDraft

  // guard init
  const initRef = useRef(false)

  useEffect(() => {
    if (initRef.current) return
    initRef.current = true

    setTransition(location.state?.transition)
    setTitle(`Edit Penerimaan Barang ${id || ""}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // kalau loader berubah (revalidation), update state
  useEffect(() => {
    setGr(grFromLoader)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grFromLoader?.id])

  /**
   * Payload GR yang dikirim ke API update.
   * Sesuaikan field kalau endpoint kamu beda.
   */
  const buildPayload = (formPayload) => {
    const items = Array.isArray(formPayload?.items)
      ? formPayload.items
      : Array.isArray(gr?.items)
      ? gr.items
      : []

    return {
      po_id: formPayload?.po_id ?? gr?.po_id ?? null,
      outlet_id: formPayload?.outlet_id ?? gr?.outlet_id ?? null,

      receipt_date: formPayload?.receipt_date ?? gr?.receipt_date ?? todayISO(),
      supplier_do_number: formPayload?.supplier_do_number ?? gr?.supplier_do_number ?? null,
      remarks: formPayload?.remarks ?? formPayload?.notes ?? gr?.remarks ?? gr?.notes ?? null,

      items: items.map((it) => ({
        id: it.id, // optional (kalau update by id)
        po_item_id: it.po_item_id ?? null, // optional
        product_unit_id: it.product_unit_id,
        qty_received: num(it.qty_received),
        batch_no: it.batch_no ?? null,
        expired_date: it.expired_date ?? null,
      })),
    }
  }

  const validate = (payload) => {
    const e = {}

    if (!payload?.po_id) e.po_id = "PO wajib ada"
    if (!payload?.outlet_id) e.outlet_id = "Outlet wajib ada"
    if (!payload?.receipt_date) e.receipt_date = "Tanggal penerimaan wajib"

    const items = Array.isArray(payload?.items) ? payload.items : []
    if (!items.length) e.items = "Minimal 1 item"

    for (const [idx, it] of items.entries()) {
      if (!it.product_unit_id) e[`items.${idx}.product_unit_id`] = "Produk wajib"
      if (num(it.qty_received) <= 0) e[`items.${idx}.qty_received`] = "Qty diterima harus > 0"
      // expired_date optional; kalau mau enforce date, enforce di service sudah ada
    }

    setErrors(e)
    return Object.keys(e).length === 0
  }

  // =========================
  // Actions (container handles)
  // =========================

  const handleSaveDraft = async (formPayload) => {
    if (isLocked) return

    const payload = buildPayload(formPayload)
    if (!validate(payload)) return

    try {
      nProgress.start()
      toast.loading("Menyimpan draft...", { id: "gr-save" })

      // ✅ update GR (header + items)
      // kalau API kamu split: updateHeader + updateItems,
      // bilang aja, nanti aku bikinin versi split.
      const res = await apiFetch(`/goods-receipts/${id}`, {
        method: "PUT",
        body: payload,
      })

      toast.success(res?.message || "GR berhasil disimpan", { id: "gr-save" })

      const next = res?.data || res
      if (next?.id) setGr(next)

      cache?.clear?.()
    } catch (e) {
      toast.error(e?.message || "Gagal save GR", { id: "gr-save" })
    } finally {
      nProgress.done()
    }
  }

  const handlePost = async () => {
    if (!isEdit) return
    if (!can?.("goods-receipt.post")) return
    if (isLocked) return // posted/cancelled

    const ok = window.confirm(`Posting GR ${gr?.gr_number || id || ""}?`)
    if (!ok) return

    try {
      nProgress.start()
      toast.loading("Posting...", { id: "gr-post" })

      const res = await apiFetch(`/goods-receipts/${id}/post`, { method: "POST", body: { postedBy: user?.name } })

      toast.success(res?.message || "GR berhasil di-post", { id: "gr-post" })
      setGr((prev) => ({ ...(prev || {}), status: "POSTED" }))

      cache?.clear?.()
    } catch (e) {
      toast.error(e?.message || "Gagal post GR", { id: "gr-post" })
    } finally {
      nProgress.done()
    }
  }

  const handleCancel = async () => {
    if (!isEdit) return
    if (!can?.("goods-receipt.cancel")) return
    if (status === "CANCELLED") return

    const reason = window.prompt("Alasan cancel (optional):", "")
    if (reason === null) return

    try {
      nProgress.start()
      toast.loading("Cancelling...", { id: "gr-cancel" })

      const res = await apiFetch(`/goods-receipts/${id}/cancel`, {
        method: "POST",
        body: { reason: reason || null, cancelledBy: user?.name },
      })

      toast.success(res?.message || "GR berhasil dibatalkan", { id: "gr-cancel" })
      setGr((prev) => ({ ...(prev || {}), status: "CANCELLED" }))

      cache?.clear?.()
    } catch (e) {
      toast.error(e?.message || "Gagal cancel GR", { id: "gr-cancel" })
    } finally {
      nProgress.done()
    }
  }

  const headerActions = useMemo(
    () => ({
      canCancel: Boolean(can?.("goods-receipt.cancel")) && status !== "CANCELLED",
      canPost: Boolean(can?.("goods-receipt.post")) && status === "DRAFT",
      canSave: Boolean(can?.("goods-receipt.edit")) || Boolean(can?.("goods-receipt.create")),

      isLocked,
      status,

      onCancel: handleCancel,
      onPost: handlePost,
      onSaveDraft: handleSaveDraft,
      onBack: () => navigate("/goods-receipt", { state: location.state }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [can, status, isLocked, id, gr?.gr_number]
  )

  return (
    <Transition type="fade">
      <Form data={gr} errors={errors} actions={headerActions} onSubmit={handleSaveDraft} />
    </Transition>
  )
}
